<template>
  <div class="json-viewer-container">
    <!-- Edit Mode Toggle -->
    <div v-if="editable" class="flex items-center justify-between mb-3">
      <span class="text-sm font-medium text-gray-700">JSON Editor</span>
      <div class="flex items-center space-x-2">
        <button
          @click="toggleEditMode"
          :class="[
            'px-3 py-1 text-xs font-medium rounded-md',
            isEditing 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          ]"
        >
          {{ isEditing ? 'View Mode' : 'Edit Mode' }}
        </button>
        <button
          v-if="isEditing"
          @click="saveChanges"
          :disabled="!hasChanges || hasError"
          class="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="hasError" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
      <div class="flex">
        <svg class="flex-shrink-0 h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <div class="ml-3">
          <p class="text-sm text-red-800">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <!-- JSON Content -->
    <div class="json-viewer">
      <!-- Edit Mode - Textarea -->
      <textarea
        v-if="isEditing"
        v-model="editableJson"
        @input="validateJson"
        class="w-full h-64 p-3 text-sm font-mono bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-none"
        :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': hasError }"
        placeholder="Enter JSON data..."
      ></textarea>

      <!-- View Mode - Formatted JSON -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="isEmpty" class="text-center py-8 text-gray-500">
          <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p class="mt-2 text-sm">No data available</p>
        </div>

        <!-- JSON Tree -->
        <div v-else class="json-tree">
          <JsonNode
            :key="jsonKey"
            :data="data"
            :path="[]"
            :expanded="expandedState"
            @toggle="toggleNode"
          />
        </div>
      </div>
    </div>

    <!-- Footer Info -->
    <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
      <div>
        <span v-if="!isEmpty">
          {{ objectCount }} {{ objectCount === 1 ? 'property' : 'properties' }}
        </span>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="collapseAll"
          class="hover:text-gray-700"
        >
          Collapse All
        </button>
        <span>•</span>
        <button
          @click="expandAll"
          class="hover:text-gray-700"
        >
          Expand All
        </button>
        <span>•</span>
        <button
          @click="copyToClipboard"
          class="hover:text-gray-700"
        >
          Copy JSON
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import JsonNode from './JsonNode.vue'

const props = defineProps({
  data: {
    type: [Object, Array, String, Number, Boolean],
    default: () => ({})
  },
  editable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update'])

// State
const isEditing = ref(false)
const editableJson = ref('')
const expandedState = ref({})
const hasError = ref(false)
const errorMessage = ref('')
const hasChanges = ref(false)

// Computed
const isEmpty = computed(() => {
  if (props.data === null || props.data === undefined) return true
  if (typeof props.data === 'object') {
    return Object.keys(props.data).length === 0
  }
  return false
})

const objectCount = computed(() => {
  if (isEmpty.value) return 0
  if (typeof props.data === 'object' && props.data !== null) {
    return Object.keys(props.data).length
  }
  return 1
})

const jsonKey = computed(() => {
  // Force re-render when data changes
  return JSON.stringify(props.data)
})

// Methods
const toggleEditMode = () => {
  isEditing.value = !isEditing.value
  if (isEditing.value) {
    editableJson.value = JSON.stringify(props.data, null, 2)
    hasChanges.value = false
    hasError.value = false
  }
}

const validateJson = () => {
  try {
    const parsed = JSON.parse(editableJson.value)
    hasError.value = false
    errorMessage.value = ''
    hasChanges.value = editableJson.value !== JSON.stringify(props.data, null, 2)
  } catch (error) {
    hasError.value = true
    errorMessage.value = `Invalid JSON: ${error.message}`
    hasChanges.value = true
  }
}

const saveChanges = () => {
  try {
    const parsed = JSON.parse(editableJson.value)
    emit('update', parsed)
    isEditing.value = false
    hasChanges.value = false
  } catch (error) {
    hasError.value = true
    errorMessage.value = `Cannot save invalid JSON: ${error.message}`
  }
}

const toggleNode = (path) => {
  const key = path.join('.')
  expandedState.value[key] = !expandedState.value[key]
}

const expandAll = () => {
  const expand = (obj, currentPath = []) => {
    if (typeof obj === 'object' && obj !== null) {
      Object.keys(obj).forEach(key => {
        const path = [...currentPath, key]
        const pathKey = path.join('.')
        expandedState.value[pathKey] = true
        expand(obj[key], path)
      })
    }
  }
  expand(props.data)
}

const collapseAll = () => {
  expandedState.value = {}
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(JSON.stringify(props.data, null, 2))
    console.log('JSON copied to clipboard')
  } catch (err) {
    console.error('Failed to copy JSON:', err)
  }
}

// Watchers

// Only update editableJson when not editing
watch(() => props.data, (newData) => {
  if (!isEditing.value) {
    editableJson.value = JSON.stringify(newData, null, 2)
    hasChanges.value = false
  }
}, { deep: true })

// Reset edit mode if editable becomes false (e.g., tab switch)
watch(() => props.editable, (newVal) => {
  if (!newVal && isEditing.value) {
    isEditing.value = false
  }
})

// Initialize expanded state for top-level properties
watch(() => props.data, (newData) => {
  if (typeof newData === 'object' && newData !== null) {
    Object.keys(newData).forEach(key => {
      if (!(key in expandedState.value)) {
        expandedState.value[key] = true
      }
    })
  }
}, { immediate: true, deep: true })
</script>

<style scoped>
.json-viewer-container {
  @apply text-sm;
}

.json-tree {
  @apply space-y-1;
  max-height: 400px;
  overflow-y: auto;
}
</style>